// Pure helpers for the Permit Sources screen: registry row <-> form state, and
// the column guesses that pre-fill the form after "Read columns".
//
// The SERVICE is the authority on whether a row can pull (validate_source in
// buildiq/app/prospects.py). Nothing here decides that; the guesses only save
// typing, and "Test pull" is what tells staff whether they were right.

export const MULTI_KEYS = ["title", "trade", "address"];
export const SINGLE_KEYS = [
  "valuation",
  "status",
  "final_on",
  "lat",
  "lon",
  "area",
  "posted_on",
];

export const MAP_LABELS = {
  title: "Title — what the job is (columns joined in order)",
  trade: "Trade / permit type",
  address: "Site address (columns joined with spaces)",
  valuation: "Valuation",
  status: "Status",
  final_on: "Finalled date — only a column set when a permit is FINISHED",
  lat: "Latitude",
  lon: "Longitude",
  area: "City column (optional; the City field is used when this is empty)",
  posted_on: "Issue date shown on the board",
};

const asList = (spec) => {
  if (!spec) return [];
  if (typeof spec === "string") return [spec];
  if (Array.isArray(spec)) return spec.filter(Boolean);
  if (typeof spec === "object" && Array.isArray(spec.cols)) return spec.cols;
  return [];
};

export const emptyForm = () => ({
  source_id: "",
  label: "",
  driver: "esri",
  base_url: "",
  portal_url: "",
  area: "",
  state: "",
  default_days: 60,
  active: true,
  page: 1000,
  date_field: "",
  order_by: "",
  id_field: "",
  id_is_number: false,
  geometry: false,
  map: {
    title: [],
    trade: [],
    address: [],
    valuation: "",
    status: "",
    final_on: "",
    lat: "",
    lon: "",
    area: "",
    posted_on: "",
  },
  // Keys in config the form does not edit (dataset, notes) ride along untouched.
  extra: {},
  isNew: true,
});

const KNOWN_CONFIG = [
  "page",
  "date_field",
  "order_by",
  "id_field",
  "id_is_number",
  "geometry",
  "fields",
  "map",
];

export function formFromSource(src) {
  const cfg = (src && src.config) || {};
  const m = cfg.map || {};
  const form = emptyForm();
  const extra = {};
  Object.keys(cfg).forEach((k) => {
    if (!KNOWN_CONFIG.includes(k)) extra[k] = cfg[k];
  });
  return {
    ...form,
    source_id: src.source_id || "",
    label: src.label || "",
    driver: src.driver || "esri",
    base_url: src.base_url || "",
    portal_url: src.portal_url || "",
    area: src.area || "",
    state: src.state || "",
    default_days: src.default_days || 60,
    active: src.active !== false,
    page: cfg.page || 1000,
    date_field: cfg.date_field || "",
    order_by: cfg.order_by || "",
    id_field: cfg.id_field || "",
    id_is_number: cfg.id_is_number === true,
    geometry: cfg.geometry === true,
    map: {
      title: asList(m.title),
      trade: asList(m.trade),
      address: asList(m.address),
      valuation: typeof m.valuation === "string" ? m.valuation : "",
      status: typeof m.status === "string" ? m.status : "",
      final_on: typeof m.final_on === "string" ? m.final_on : "",
      lat: typeof m.lat === "string" ? m.lat : "",
      lon: typeof m.lon === "string" ? m.lon : "",
      area: typeof m.area === "string" ? m.area : "",
      posted_on: typeof m.posted_on === "string" ? m.posted_on : "",
    },
    extra,
    isNew: false,
  };
}

// The registry row the service stores. `fields` (Esri's outFields) is every
// column the map reads plus the id and date — asking for "*" works too, but a
// named list keeps `raw` on each listing to what the row is actually about.
export function sourceFromForm(form) {
  const map = {};
  if (form.map.title.length) map.title = [...form.map.title];
  if (form.map.trade.length) map.trade = [...form.map.trade];
  if (form.map.address.length === 1) map.address = form.map.address[0];
  if (form.map.address.length > 1)
    map.address = { cols: [...form.map.address], sep: " " };
  SINGLE_KEYS.forEach((k) => {
    if (form.map[k]) map[k] = form.map[k];
  });

  const config = { ...form.extra, page: Number(form.page) || 1000, map };
  if (form.id_field) config.id_field = form.id_field;
  if (form.date_field) config.date_field = form.date_field;
  config.order_by = form.order_by || form.id_field || form.date_field || undefined;
  if (!config.order_by) delete config.order_by;

  if (form.driver === "esri") {
    config.id_is_number = !!form.id_is_number;
    config.geometry = !!form.geometry;
    const cols = [
      form.id_field,
      form.date_field,
      ...form.map.title,
      ...form.map.trade,
      ...form.map.address,
      ...SINGLE_KEYS.map((k) => form.map[k]),
    ].filter(Boolean);
    config.fields = [...new Set(cols)];
  }

  return {
    source_id: form.source_id.trim(),
    label: form.label.trim(),
    driver: form.driver,
    base_url: form.base_url.trim(),
    portal_url: form.portal_url.trim() || null,
    area: form.area.trim(),
    state: form.state.trim().toUpperCase(),
    default_days: Number(form.default_days) || 60,
    active: !!form.active,
    config,
  };
}

// "Las Vegas" -> "las_vegas_permits". A suggestion only; an existing city's id
// is never changed, because every listing and mark is keyed to it.
export function suggestSourceId(area, suffix = "permits") {
  const slug = String(area || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return slug ? `${slug}_${suffix}`.slice(0, 60) : "";
}

const NUMERIC_TYPES = ["integer", "smallinteger", "biginteger", "double", "oid"];

// Pre-fill from a feed's column names. Deliberately conservative: a WRONG guess
// is worse than an empty select, because an empty select is obviously
// unfinished and a wrong one looks done. Every guess still goes through Test pull.
export function guessFromColumns(columns, driver) {
  const names = (columns || []).map((c) => c.name);
  const typeOf = (n) =>
    ((columns || []).find((c) => c.name === n) || {}).type || "";
  const first = (...patterns) => {
    for (const re of patterns) {
      const hit = names.find((n) => re.test(n));
      if (hit) return hit;
    }
    return "";
  };
  const isDate = (n) => typeOf(n) === "date" || driver === "socrata";

  const id_field = first(
    /^permit_?(num|number|nbr|no)$/i,
    /^permit_$/i,
    /^apno$/i,
    /^number$/i,
    /permit.?(num|number|nbr)/i,
  );
  const dateCandidates = names.filter(
    (n) => /iss(ue)?(d)?_?(date|dt|dttm|dtm)|^issdate$/i.test(n) && isDate(n),
  );
  const date_field =
    dateCandidates.find((n) => typeOf(n) === "date") || dateCandidates[0] || "";

  const description = first(
    /^(project_)?description(_of_work)?$/i,
    /work_?desc(ription)?/i,
  );
  const workType = first(/^work_?(class|type)$/i, /type_of_work/i, /sub_?type/i);
  const title = [workType, description].filter(Boolean);

  const lat = first(/^lat(itude)?$/i);
  const lon = first(/^lon(g|gitude)?$/i);

  return {
    id_field,
    id_is_number: NUMERIC_TYPES.includes(typeOf(id_field)),
    date_field,
    map: {
      title,
      trade: [first(/^permit_?type$/i), workType].filter(
        (v, i, a) => v && a.indexOf(v) === i,
      ),
      address: [
        first(/^(project_|primary_|property_|original)?address(1|_?full)?$/i),
      ].filter(Boolean),
      valuation: first(
        /valuation/i,
        /^(est_?project_?cost|reported_cost|job_value|value)$/i,
      ),
      status: first(/^(permit_?)?status(_?current|_?desc)?$/i, /status/i),
      final_on: "",
      lat,
      lon,
      area: "",
      posted_on: date_field,
    },
  };
}

// Fill only what the form has not already got. Guessing over a value somebody
// chose would undo their work silently.
export function applyGuesses(form, guesses) {
  const next = { ...form, map: { ...form.map } };
  ["id_field", "date_field"].forEach((k) => {
    if (!next[k] && guesses[k]) next[k] = guesses[k];
  });
  if (!form.id_field && guesses.id_field) next.id_is_number = guesses.id_is_number;
  MULTI_KEYS.forEach((k) => {
    if (!next.map[k].length && guesses.map[k].length)
      next.map[k] = [...guesses.map[k]];
  });
  SINGLE_KEYS.forEach((k) => {
    if (!next.map[k] && guesses.map[k]) next.map[k] = guesses.map[k];
  });
  return next;
}
